import { useState, useMemo, useRef } from 'react'
import { ReactComponent as IconClose } from 'bootstrap-icons/icons/x-lg.svg'
import { ReactComponent as IconTrash } from 'bootstrap-icons/icons/trash.svg'
import { ReactComponent as IconEyeSlash } from 'bootstrap-icons/icons/eye-slash.svg'
import { ReactComponent as IconEye } from 'bootstrap-icons/icons/eye.svg'

import { useApi, paths } from './hooks'
import styles from './styles.module.scss'

const TYPES = [
  ['text', 'Texto'], ['paragraph', 'Párrafo'], ['number', 'Número'], ['date', 'Fecha'],
  ['boolean', 'Sí / No'], ['single_choice', 'Opción única'], ['multiple_choice', 'Opción múltiple'],
  ['scale', 'Escala'], ['file', 'Archivo'], ['calculated', 'Calculado'],
]
const genId = () => 'c_' + Math.random().toString(36).slice(2, 8)
const typeLabel = (type) => (TYPES.find(([v]) => v === type) || [type, type])[1]

function ColumnRow({ col, onChange, onToggleHide, onDelete, allowDelete, locked, invalid, labelRef }) {
  const set = (k, v) => onChange({ ...col, [k]: v })
  const needsOptions = col.type === 'single_choice' || col.type === 'multiple_choice'
  const needsRange = col.type === 'number' || col.type === 'scale'
  const options = col.options || []

  if (col.hidden) {
    return (
      <div className={styles.colRow} style={{ opacity: 0.6 }}>
        <div className="d-flex gap-2 align-items-center">
          <span style={{ flex: 1 }}>{col.label || 'Columna'} <span className={`${styles.chip} ${styles.chipOff}`}>Oculta</span></span>
          <button type="button" className={styles.iconbtn} onClick={onToggleHide} title="Mostrar columna"><IconEye width="14" height="14" /></button>
          {allowDelete && (
            <button type="button" className={styles.iconbtn} onClick={onDelete} title="Borrar columna"><IconTrash width="14" height="14" /></button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={styles.colRow}>
      {/* Bug real (auditoría 2026-08-21): input (flex:1) + select (maxWidth
          175, "Opción múltiple" como opción más larga) + botón, sin
          flex-wrap — ninguno de los dos primeros se achica bien por debajo
          de su contenido en flexbox, así que en mobile se salían del ancho
          de la card en vez de bajar de línea. */}
      <div className="d-flex gap-2 align-items-center flex-wrap">
        {locked ? (
          <>
            <span style={{ flex: 1, fontWeight: 600 }}>{col.label || 'Columna'}</span>
            <span className={`${styles.chip} ${styles.chipGray}`}>{typeLabel(col.type)}</span>
          </>
        ) : (
          <>
            <input ref={labelRef} className={`form-control form-control-sm ${invalid ? 'is-invalid' : ''}`} style={{ flex: 1 }} placeholder="Nombre de la columna"
              value={col.label || ''} onChange={(e) => set('label', e.target.value)} />
            <select className="form-select form-select-sm" style={{ maxWidth: 175 }} value={col.type} onChange={(e) => set('type', e.target.value)}>
              {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </>
        )}
        {allowDelete ? (
          <button type="button" className={styles.iconbtn} onClick={onDelete} title="Borrar columna"><IconTrash width="14" height="14" /></button>
        ) : (
          <button type="button" className={styles.iconbtn} onClick={onToggleHide} title="Ocultar columna"><IconEyeSlash width="14" height="14" /></button>
        )}
      </div>
      {invalid && <div className="text-danger small mt-1">Al menos una columna necesita un nombre.</div>}

      {needsRange && (
        <div className="d-flex gap-2 mt-2">
          {col.type === 'number' && (
            <input className="form-control form-control-sm" style={{ maxWidth: 100 }} placeholder="Unidad"
              value={col.unit || ''} onChange={(e) => set('unit', e.target.value)} />
          )}
          <input className="form-control form-control-sm" type="number" style={{ maxWidth: 80 }} placeholder="Min"
            value={col.min ?? ''} onChange={(e) => set('min', e.target.value === '' ? undefined : Number(e.target.value))} />
          <input className="form-control form-control-sm" type="number" style={{ maxWidth: 80 }} placeholder="Max"
            value={col.max ?? ''} onChange={(e) => set('max', e.target.value === '' ? undefined : Number(e.target.value))} />
        </div>
      )}

      {needsOptions && (
        <div className="mt-2 ms-1">
          {options.map((opt, i) => (
            <div key={i} className="d-flex gap-1 mb-1">
              <input className="form-control form-control-sm" placeholder={`Opción ${i + 1}`} value={opt.label || ''}
                onChange={(e) => {
                  const label = e.target.value
                  const next = [...options]
                  next[i] = { label, value: label }
                  set('options', next)
                }} />
              <button type="button" className={styles.iconbtn} onClick={() => set('options', options.filter((_, j) => j !== i))}><IconTrash width="13" height="13" /></button>
            </div>
          ))}
          <button type="button" className={`${styles.btn} ${styles.ghost}`} onClick={() => set('options', [...options, { label: '', value: '' }])}>+ Agregar opción</button>
        </div>
      )}

      {col.type === 'calculated' && (
        <div className="mt-2" style={{ background: '#e7f6fc', border: '1px solid #9edcf1', borderRadius: 8, padding: '.5rem .6rem' }}>
          <div className={styles.fieldLabel} style={{ color: '#076585' }}>Fórmula</div>
          <input className="form-control form-control-sm" style={{ fontFamily: 'Roboto Mono, monospace' }} placeholder="Ej: FUM + 280"
            value={col.formula || ''} onChange={(e) => set('formula', e.target.value)} disabled={locked} />
          <div className={styles.note} style={{ marginTop: 4 }}>Referencia a otra columna ± número (fecha en días, o suma/resta simple).</div>
        </div>
      )}

      <label className="d-flex align-items-center gap-2 mt-2" style={{ fontSize: '.8rem' }}>
        <input type="checkbox" checked={!!col.required} onChange={(e) => set('required', e.target.checked)} /> Obligatoria
      </label>
    </div>
  )
}

function CustomFormBuilder({ form, resource, onBack, onSaved }) {
  const api = useApi()
  const editing = !!form?.id
  const lockedIds = useMemo(
    () => new Set((form?.columns || []).map((c) => c.id).filter(Boolean)),
    [form?.columns]
  )

  const [name, setName] = useState(form?.name || '')
  const [professionalScope, setProfessionalScope] = useState(form ? (form.professionals == null ? 'all' : 'owner') : 'owner')
  const [specialtyScope, setSpecialtyScope] = useState(form ? (form.specialties == null ? 'todas' : 'specific') : 'todas')
  const [specialties, setSpecialties] = useState(form?.specialties || [])
  const [columns, setColumns] = useState(form?.columns?.length ? form.columns : [{ id: genId(), type: 'text', label: '' }])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [invalidName, setInvalidName] = useState(false)
  const [invalidColumnIdx, setInvalidColumnIdx] = useState(null)
  const nameRef = useRef(null)
  const columnRefs = useRef([])
  const [showHidden, setShowHidden] = useState(false)
  const hiddenCount = columns.filter((c) => c.hidden).length

  const resourceSpecialties = Array.isArray(resource?.specialties) ? resource.specialties : []

  const updateColumn = (i, col) => {
    setColumns((cols) => cols.map((c, j) => (j === i ? col : c)))
    if (invalidColumnIdx === i && (col.label || '').trim()) setInvalidColumnIdx(null)
  }
  const toggleHideColumn = (i) => setColumns((cols) => cols.map((c, j) => (j === i ? { ...c, hidden: !c.hidden } : c)))
  const deleteColumn = (i) => setColumns((cols) => cols.filter((_, j) => j !== i))
  const addColumn = () => setColumns((cols) => [...cols, { id: genId(), type: 'text', label: '' }])
  const toggleSpecialty = (id) => setSpecialties((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))

  // Qué bloquea el guardado: falta el nombre del formulario, o hay alguna columna
  // (visible) sin nombre. Avisa con foco + rojo en el primer campo faltante, en vez
  // de dejar el botón deshabilitado sin feedback.
  const validate = () => {
    if (!name.trim()) return { name: true }
    const columnIdx = columns.findIndex((c) => !c.hidden && !(c.label || '').trim())
    if (columnIdx !== -1) return { columnIdx }
    return null
  }

  const save = async () => {
    const invalid = validate()
    if (invalid) {
      setInvalidName(!!invalid.name)
      setInvalidColumnIdx(invalid.columnIdx ?? null)
      if (invalid.name) nameRef.current?.focus()
      else columnRefs.current[invalid.columnIdx]?.focus()
      return
    }
    setInvalidName(false); setInvalidColumnIdx(null)
    setSaving(true); setError(null)
    try {
      const body = {
        name: name.trim(),
        columns: columns.map((c, i) => ({ ...c, order: i })),
        professionalScope,
        specialtyScope,
        specialties: specialtyScope === 'specific' ? specialties : [],
      }
      const saved = editing
        ? await api(paths.update(form.id), { method: 'PATCH', body })
        : await api(paths.create(), { method: 'POST', body })
      onSaved(saved)
    } catch (e) { setError(e.message); setSaving(false) }
  }

  const disable = async () => {
    setSaving(true); setError(null)
    try {
      await api(paths.disable(form.id), { method: 'POST' })
      onSaved(null)
    } catch (e) { setError(e.message); setSaving(false) }
  }

  return (
    <div className={styles.overlay}>
      <div className={`${styles.modal} ${styles.modalWide}`} onClick={(e) => e.stopPropagation()}>
        <div className={styles.head}>
          <div className={styles.title}>{editing ? 'Configurar formulario' : 'Nuevo formulario'}</div>
          <div className={styles.spacer} />
          <button type="button" className={styles.iconbtn} onClick={onBack} aria-label="Cerrar"><IconClose width="16" height="16" /></button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.field} style={{ maxWidth: 440 }}>
            <label className={styles.fieldLabel}>Nombre del formulario<span className={styles.req}>*</span></label>
            <input
              ref={nameRef}
              className={`form-control ${invalidName ? 'is-invalid' : ''}`}
              value={name}
              onChange={(e) => { setName(e.target.value); if (invalidName && e.target.value.trim()) setInvalidName(false) }}
              placeholder="Ej: Control de peso"
            />
            {invalidName && <div className="invalid-feedback">Ponele un nombre al formulario para poder crearlo.</div>}
          </div>

          <div className={styles.scopeBox}>
            <div className={styles.fieldLabel}>Alcance <span className={styles.note}>(combinable)</span></div>
            <div className={styles.note} style={{ marginBottom: 4 }}>¿Quién puede usarlo?</div>
            <label className="d-flex align-items-center gap-2" style={{ fontSize: '.85rem' }}>
              <input type="radio" name="who" checked={professionalScope === 'owner'} onChange={() => setProfessionalScope('owner')} /> Solo yo
            </label>
            <label className="d-flex align-items-center gap-2 mb-2" style={{ fontSize: '.85rem' }}>
              <input type="radio" name="who" checked={professionalScope === 'all'} onChange={() => setProfessionalScope('all')} /> Todos los profesionales del team
            </label>
            <div className={styles.note} style={{ marginBottom: 4 }}>¿En qué especialidades?</div>
            <label className="d-flex align-items-center gap-2" style={{ fontSize: '.85rem' }}>
              <input type="radio" name="spec" checked={specialtyScope === 'todas'} onChange={() => setSpecialtyScope('todas')} /> Todas las especialidades
            </label>
            <label className="d-flex align-items-center gap-2" style={{ fontSize: '.85rem' }}>
              <input type="radio" name="spec" checked={specialtyScope === 'specific'} onChange={() => setSpecialtyScope('specific')} /> Específicas
            </label>
            {specialtyScope === 'specific' && (
              <div className={styles.choice} style={{ marginTop: 6 }}>
                {resourceSpecialties.length === 0 && <span className={styles.note}>No tenés especialidades cargadas.</span>}
                {resourceSpecialties.map((s) => (
                  <button key={s.id} type="button" className={`${styles.opt} ${specialties.includes(s.id) ? styles.optOn : ''}`} onClick={() => toggleSpecialty(s.id)}>{s.label}</button>
                ))}
              </div>
            )}
          </div>

          <div className="d-flex align-items-center justify-content-between" style={{ marginBottom: '.5rem' }}>
            <div className={styles.fieldLabel} style={{ marginBottom: 0 }}>Columnas</div>
            {hiddenCount > 0 && (
              <label className="d-flex align-items-center gap-2" style={{ fontSize: '.78rem', fontWeight: 400, color: '#6b7280' }}>
                <input type="checkbox" checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} />
                Mostrar campos deshabilitados ({hiddenCount})
              </label>
            )}
          </div>
          <div className={styles.builderCols}>
            {columns.map((col, i) => {
              if (col.hidden && !showHidden) return null
              const locked = editing && lockedIds.has(col.id)
              const allowDelete = !locked
              return (
                <ColumnRow
                  key={col.id}
                  col={col}
                  locked={!!locked}
                  allowDelete={allowDelete}
                  invalid={invalidColumnIdx === i}
                  labelRef={(el) => { columnRefs.current[i] = el }}
                  onChange={(c) => updateColumn(i, c)}
                  onToggleHide={() => toggleHideColumn(i)}
                  onDelete={() => deleteColumn(i)}
                />
              )
            })}
          </div>

          <div className={styles.builderFooter}>
            <button type="button" className={`${styles.btn} ${styles.outline}`} onClick={addColumn}>+ Columna</button>
          </div>

          {error && <div className="text-danger small mt-2">Error al guardar: {error}</div>}
        </div>

        <div className={styles.modalActions}>
          {editing && !form.disabled && (
            <button type="button" className={`${styles.btn} ${styles.danger} ${styles.modalActionsLeft}`} disabled={saving} onClick={disable}>
              <IconEyeSlash width="13" height="13" /> Ocultar
            </button>
          )}
          <button type="button" className={`${styles.btn} ${styles.ghost}`} disabled={saving} onClick={onBack}>Cancelar</button>
          <button type="button" className={`${styles.btn} ${styles.primary}`} disabled={saving} onClick={save}>
            {saving ? 'Guardando…' : editing ? 'Guardar cambios' : 'Crear formulario'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default CustomFormBuilder
